import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db, functions, storage } from '@/firebase';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';

interface SyncResult {
    success: boolean;
    results: {
        created: number;
        updated: number;
        errors: number;
        errorDetails: string[];
    };
}

interface Shikaku {
    id: string;
    ShikakuName: string;
    Lv1?: string;   // 0-69
    Lv70?: string;  // 70-79
    Lv80?: string;  // 80-89
    Lv90?: string;  // 90-94
    Lv95?: string;  // 95-99
    Lv100?: string; // 100
    pdfUrl?: string;
    pdfFileName?: string;
}

export default function ShikakuManagement() {
    const navigate = useNavigate();
    const [shikakuList, setShikakuList] = useState<Shikaku[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [uploadingPDF, setUploadingPDF] = useState(false);

    // Form State
    const [formData, setFormData] = useState<Omit<Shikaku, 'id'>>({
        ShikakuName: '',
        Lv1: '',
        Lv70: '',
        Lv80: '',
        Lv90: '',
        Lv95: '',
        Lv100: '',
        pdfUrl: '',
        pdfFileName: ''
    });

    const fetchShikaku = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, 'Shikaku'));
            const list = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ShikakuName: doc.data().ShikakuName || '',
                Lv1: doc.data().Lv1 || '',
                Lv70: doc.data().Lv70 || '',
                Lv80: doc.data().Lv80 || '',
                Lv90: doc.data().Lv90 || '',
                Lv95: doc.data().Lv95 || '',
                Lv100: doc.data().Lv100 || '',
                pdfUrl: doc.data().pdfUrl || '',
                pdfFileName: doc.data().pdfFileName || '',
            }));
            setShikakuList(list);
        } catch (error) {
            console.error("Error fetching shikaku:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchShikaku();
    }, []);

    const handleOpenModal = (shikaku?: Shikaku) => {
        if (shikaku) {
            setEditingId(shikaku.id);
            setFormData({
                ShikakuName: shikaku.ShikakuName,
                Lv1: shikaku.Lv1 || '',
                Lv70: shikaku.Lv70 || '',
                Lv80: shikaku.Lv80 || '',
                Lv90: shikaku.Lv90 || '',
                Lv95: shikaku.Lv95 || '',
                Lv100: shikaku.Lv100 || '',
                pdfUrl: shikaku.pdfUrl || '',
                pdfFileName: shikaku.pdfFileName || ''
            });
        } else {
            setEditingId(null);
            setFormData({
                ShikakuName: '',
                Lv1: '',
                Lv70: '',
                Lv80: '',
                Lv90: '',
                Lv95: '',
                Lv100: '',
                pdfUrl: '',
                pdfFileName: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingId(null);
    };

    const handlePDFUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !editingId) return;

        if (!file.type.includes('pdf')) {
            alert('PDFファイルのみアップロード可能です');
            return;
        }

        try {
            setUploadingPDF(true);
            const storageRef = ref(storage, `shikaku-pdfs/${editingId}/${file.name}`);
            await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(storageRef);

            setFormData(prev => ({
                ...prev,
                pdfUrl: downloadUrl,
                pdfFileName: file.name
            }));

            alert('PDFがアップロードされました');
        } catch (error) {
            console.error("Error uploading PDF:", error);
            alert('PDFのアップロードに失敗しました');
        } finally {
            setUploadingPDF(false);
        }
    };

    const handleDeletePDF = async () => {
        if (!editingId || !formData.pdfUrl) return;

        try {
            setUploadingPDF(true);
            const fileRef = ref(storage, `shikaku-pdfs/${editingId}/${formData.pdfFileName}`);
            await deleteObject(fileRef);

            setFormData(prev => ({
                ...prev,
                pdfUrl: '',
                pdfFileName: ''
            }));

            alert('PDFが削除されました');
        } catch (error) {
            console.error("Error deleting PDF:", error);
            alert('PDFの削除に失敗しました');
        } finally {
            setUploadingPDF(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.ShikakuName.trim()) return;

        try {
            if (editingId) {
                // Update
                await updateDoc(doc(db, 'Shikaku', editingId), {
                    ...formData
                });
            } else {
                // Add
                await addDoc(collection(db, 'Shikaku'), {
                    ...formData
                });
            }
            handleCloseModal();
            fetchShikaku();
        } catch (error) {
            console.error("Error saving shikaku:", error);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('本当に削除しますか?')) return;
        try {
            await deleteDoc(doc(db, 'Shikaku', id));
            fetchShikaku();
        } catch (error) {
            console.error("Error deleting shikaku:", error);
        }
    };

    const handleSync = async () => {
        if (!confirm('スプレッドシートからデータを同期しますか?\n既存のデータは上書きされます。')) return;

        setIsSyncing(true);
        try {
            const syncFunction = httpsCallable(functions, 'syncShikakuFromSheet');
            const result = await syncFunction();

            const data = result.data as SyncResult;
            setSyncResult(data);
            alert(`同期完了!\n新規作成: ${data.results.created}件\n更新: ${data.results.updated}件\nエラー: ${data.results.errors}件`);

            // Refresh list
            fetchShikaku();
        } catch (error: any) {
            console.error('Sync error:', error);
            alert(`同期に失敗しました: ${error.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <div className="">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-2xl font-bold text-gray-800">資格管理</h1>
                    <div className="flex gap-4">
                        <button
                            onClick={handleSync}
                            disabled={isSyncing}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSyncing ? (
                                <>
                                    <span className="animate-spin">⟳</span>
                                    同期中...
                                </>
                            ) : (
                                <>
                                    📊 スプレッドシートから同期
                                </>
                            )}
                        </button>
                        {/* Back button removed */}
                    </div>
                </div>

                {/* Add Button */}
                <div className="mb-8">
                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 cursor-pointer flex items-center gap-2"
                    >
                        <i className="ri-add-line"></i> 新規資格追加
                    </button>
                </div>

                <div className="mb-4 text-sm text-gray-600 font-medium">
                    現在の表示件数: {shikakuList.length} 件
                </div>

                {/* List */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">資格名</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={3} className="px-6 py-4 text-center">読み込み中...</td></tr>
                            ) : shikakuList.map((shikaku) => (
                                <tr key={shikaku.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{shikaku.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                        {shikaku.ShikakuName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end gap-4">
                                            <button onClick={() => handleOpenModal(shikaku)} className="text-blue-600 hover:text-blue-900 cursor-pointer">編集</button>
                                            <button onClick={() => handleDelete(shikaku.id)} className="text-red-600 hover:text-red-900 cursor-pointer">削除</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Sync Result Modal */}
            {syncResult && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">同期結果</h2>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">新規作成:</span>
                                <span className="font-bold text-green-600">{syncResult.results.created}件</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">更新:</span>
                                <span className="font-bold text-blue-600">{syncResult.results.updated}件</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-700">エラー:</span>
                                <span className="font-bold text-red-600">{syncResult.results.errors}件</span>
                            </div>
                            {syncResult.results.errorDetails && syncResult.results.errorDetails.length > 0 && (
                                <div className="mt-4">
                                    <p className="text-sm font-medium text-gray-700 mb-2">エラー詳細:</p>
                                    <div className="bg-red-50 border border-red-200 rounded p-3 max-h-40 overflow-y-auto">
                                        {syncResult.results.errorDetails.map((error: string, index: number) => (
                                            <p key={index} className="text-sm text-red-700">{error}</p>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setSyncResult(null)}
                            className="mt-6 w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            閉じる
                        </button>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingId ? '資格を編集' : '新規資格追加'}
                            </h2>
                            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                                <i className="ri-close-line text-2xl"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="p-6 space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">資格名 <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    required
                                    value={formData.ShikakuName}
                                    onChange={(e) => setFormData({ ...formData, ShikakuName: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                                    placeholder="例: メンタル心理カウンセラー"
                                />
                            </div>

                            {/* PDF Upload Section */}
                            {editingId && (
                                <div className="space-y-4">
                                    <h3 className="font-medium text-gray-900 border-b pb-2">テキストPDF管理</h3>
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        {formData.pdfUrl ? (
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between bg-white p-3 rounded border border-blue-200">
                                                    <div className="flex items-center gap-3">
                                                        <i className="ri-file-pdf-line text-red-500 text-xl"></i>
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{formData.pdfFileName}</p>
                                                            <p className="text-xs text-gray-500">アップロード済み</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleDeletePDF}
                                                    disabled={uploadingPDF}
                                                    className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:bg-gray-200 disabled:text-gray-500 transition-colors"
                                                >
                                                    {uploadingPDF ? 'Processing...' : 'PDFを削除'}
                                                </button>
                                                <p className="text-xs text-gray-600">別のPDFをアップロードすると置き換わります</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <label className="flex flex-col items-center justify-center border-2 border-dashed border-blue-300 rounded-lg p-6 cursor-pointer hover:bg-blue-100 transition-colors">
                                                    <i className="ri-upload-cloud-2-line text-blue-500 text-3xl mb-2"></i>
                                                    <span className="text-sm font-medium text-gray-700">PDFをアップロード</span>
                                                    <span className="text-xs text-gray-500 mt-1">または、ここにドラッグ＆ドロップ</span>
                                                    <input
                                                        type="file"
                                                        accept=".pdf"
                                                        onChange={handlePDFUpload}
                                                        disabled={uploadingPDF}
                                                        className="hidden"
                                                    />
                                                </label>
                                                {uploadingPDF && <p className="text-sm text-blue-600">アップロード中...</p>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-4">
                                <h3 className="font-medium text-gray-900 border-b pb-2">結果コメント設定</h3>

                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lv1 (0-69点)</label>
                                        <textarea
                                            value={formData.Lv1}
                                            onChange={(e) => setFormData({ ...formData, Lv1: e.target.value })}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 h-20"
                                            placeholder="不合格時のコメント"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lv70 (70-79点)</label>
                                        <textarea
                                            value={formData.Lv70}
                                            onChange={(e) => setFormData({ ...formData, Lv70: e.target.value })}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 h-20"
                                            placeholder="合格ラインギリギリのコメント"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lv80 (80-89点)</label>
                                        <textarea
                                            value={formData.Lv80}
                                            onChange={(e) => setFormData({ ...formData, Lv80: e.target.value })}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 h-20"
                                            placeholder="良判定のコメント"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lv90 (90-94点)</label>
                                        <textarea
                                            value={formData.Lv90}
                                            onChange={(e) => setFormData({ ...formData, Lv90: e.target.value })}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 h-20"
                                            placeholder="優判定のコメント"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lv95 (95-99点)</label>
                                        <textarea
                                            value={formData.Lv95}
                                            onChange={(e) => setFormData({ ...formData, Lv95: e.target.value })}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 h-20"
                                            placeholder="最優秀に近いコメント"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lv100 (100点)</label>
                                        <textarea
                                            value={formData.Lv100}
                                            onChange={(e) => setFormData({ ...formData, Lv100: e.target.value })}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500 h-20"
                                            placeholder="満点のコメント"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer"
                                >
                                    キャンセル
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
                                >
                                    保存
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
